(() => {
  const RECENTLY_VIEWED_KEY = 'recently_viewed_products';
  const productRequests = new Map();

  const safeHandle = (value) =>
    typeof value === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(value) ? value : null;

  const productUrl = (product) => {
    const handle = safeHandle(product?.handle);
    return handle ? `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}` : '#';
  };

  const productImage = (product) => {
    const value =
      (typeof product?.featured_image === 'string' ? product.featured_image : product?.featured_image?.src) ||
      product?.images?.[0]?.src ||
      product?.images?.[0];
    if (!value) return null;

    try {
      const url = new URL(value, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return null;
      url.searchParams.set('width', '320');
      return url.href;
    } catch (_error) {
      return null;
    }
  };

  const formatMoney = (cents) => {
    const amount = Number(cents) / 100;
    const currency = window.Shopify?.currency?.active || 'UAH';
    const locale = document.documentElement.lang || 'uk-UA';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (_error) {
      return `${amount.toFixed(2)} ${currency}`;
    }
  };

  const fetchProduct = (handle) => {
    if (!productRequests.has(handle)) {
      const root = window.Shopify?.routes?.root || '/';
      const request = fetch(`${root}products/${encodeURIComponent(handle)}.js`)
        .then((response) => (response.ok ? response.json() : null))
        .catch((error) => {
          if (error.name !== 'AbortError') console.error(`Unable to refresh recently viewed product ${handle}`, error);
          return null;
        });
      productRequests.set(handle, request);
    }
    return productRequests.get(handle);
  };

  if (!customElements.get('cart-recovery-recently-viewed')) {
    customElements.define(
      'cart-recovery-recently-viewed',
      class CartRecoveryRecentlyViewed extends HTMLElement {
        connectedCallback() {
          this.render();
        }

        getStoredProducts() {
          try {
            const stored = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
            if (!Array.isArray(stored)) return [];

            const unique = new Map();
            stored.forEach((entry) => {
              const normalized = typeof entry === 'string' ? { handle: entry } : entry;
              const handle = safeHandle(normalized?.handle);
              if (handle && !unique.has(handle)) unique.set(handle, normalized);
            });
            return [...unique.values()];
          } catch (_error) {
            return [];
          }
        }

        async render() {
          const list = this.querySelector('[data-recently-viewed-list]');
          if (!list) return;

          const limit = Math.min(Math.max(Number.parseInt(this.dataset.limit, 10) || 3, 2), 4);
          const stored = this.getStoredProducts().slice(0, limit + 2);
          if (!stored.length) return;

          const refreshed = await Promise.all(
            stored.map(async (entry) => (await fetchProduct(entry.handle)) || entry),
          );
          if (!this.isConnected) return;

          const products = refreshed
            .filter((product) => product?.available !== false && safeHandle(product?.handle) && product?.title)
            .slice(0, limit);
          if (!products.length) return;

          const fragment = document.createDocumentFragment();
          products.forEach((product) => fragment.appendChild(this.createCard(product)));
          list.replaceChildren(fragment);
          this.hidden = false;
        }

        createCard(product) {
          const item = document.createElement('div');
          item.className = 'cart-recovery-recently-viewed__item';
          item.setAttribute('role', 'listitem');

          const card = document.createElement('a');
          card.className = 'cart-recovery-recent-card';
          card.href = productUrl(product);

          const media = document.createElement('span');
          media.className = 'cart-recovery-recent-card__media';
          const imageUrl = productImage(product);
          if (imageUrl) {
            const image = document.createElement('img');
            image.className = 'cart-recovery-recent-card__image';
            image.src = imageUrl;
            image.alt = product.title;
            image.loading = 'lazy';
            image.decoding = 'async';
            image.width = 320;
            image.height = 320;
            media.appendChild(image);
          }

          const title = document.createElement('span');
          title.className = 'cart-recovery-recent-card__title';
          title.textContent = product.title;

          const price = document.createElement('span');
          price.className = 'cart-recovery-recent-card__price';
          price.textContent = formatMoney(product.price);

          card.append(media, title, price);
          item.appendChild(card);
          return item;
        }
      },
    );
  }

  if (!customElements.get('cart-recovery-addon')) {
    customElements.define(
      'cart-recovery-addon',
      class CartRecoveryAddon extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('[data-cart-recovery-addon-form]');
          this.form?.addEventListener('submit', this.onSubmit);
        }

        disconnectedCallback() {
          this.form?.removeEventListener('submit', this.onSubmit);
        }

        onSubmit = async (event) => {
          event.preventDefault();
          const button = this.form?.querySelector('[type="submit"]');
          const variantId = Number(this.form?.querySelector('[name="id"]')?.value);
          if (!button || !variantId || button.disabled) return;

          const errorElement = this.querySelector('[data-cart-recovery-addon-error]');
          if (errorElement) {
            errorElement.hidden = true;
            errorElement.textContent = '';
          }

          button.disabled = true;
          this.classList.add('is-loading');
          this.setAttribute('aria-busy', 'true');

          try {
            const sectionIds = this.getSectionIds();
            const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({
                items: [{ id: variantId, quantity: 1 }],
                sections: sectionIds,
                sections_url: window.location.pathname,
              }),
            });
            const state = await response.json();
            if (!response.ok || state.status) throw new Error(state.description || state.message || window.cartStrings?.error);

            if (this.closest('cart-drawer')) {
              this.renderCartPageSections(state.sections || {});
              const drawer = document.querySelector('cart-drawer');
              drawer?.renderContents(state);
            } else {
              this.renderCartPageSections(state.sections || {});
            }
          } catch (error) {
            if (errorElement) {
              errorElement.textContent = error.message || window.cartStrings?.error || 'Unable to update cart.';
              errorElement.hidden = false;
            }
            button.disabled = false;
            this.classList.remove('is-loading');
            this.removeAttribute('aria-busy');
          }
        };

        getSectionIds() {
          const ids = new Set(['cart-icon-bubble']);
          const drawer = document.querySelector('cart-drawer');
          if (drawer) ids.add('cart-drawer');

          const mainItems = document.getElementById('main-cart-items');
          const mainFooter = document.getElementById('main-cart-footer');
          if (mainItems?.dataset.id) ids.add(mainItems.dataset.id);
          if (mainFooter?.dataset.id) ids.add(mainFooter.dataset.id);
          return [...ids];
        }

        renderCartPageSections(sections) {
          const mainItems = document.getElementById('main-cart-items');
          const mainFooter = document.getElementById('main-cart-footer');

          if (mainItems?.dataset.id) {
            this.replaceInnerHTML(sections[mainItems.dataset.id], '#main-cart-items .js-contents');
            mainItems.closest('cart-items')?.classList.remove('is-empty');
          }

          if (mainFooter?.dataset.id) {
            this.replaceInnerHTML(sections[mainFooter.dataset.id], '#main-cart-footer .js-contents');
            mainFooter.classList.remove('is-empty');
          }

          const bubbleHTML = sections['cart-icon-bubble'];
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubbleHTML && bubble) {
            const source = new DOMParser().parseFromString(bubbleHTML, 'text/html').querySelector('.shopify-section');
            if (source) bubble.innerHTML = source.innerHTML;
          }

          const liveRegion = document.getElementById('cart-live-region-text');
          if (liveRegion) liveRegion.setAttribute('aria-hidden', 'false');
        }

        replaceInnerHTML(sectionHTML, selector) {
          if (!sectionHTML) return;
          const source = new DOMParser().parseFromString(sectionHTML, 'text/html').querySelector(selector);
          const target = document.querySelector(selector);
          if (source && target) target.innerHTML = source.innerHTML;
        }
      },
    );
  }
})();
